import { useState } from "react";
import { ArrowDown, ArrowUp, Check, Filter, ListFilter } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Multi-select keeps the menu open and stores the picks as a separator-joined value, so the
// query string stays a plain string all the way to the API.
const picksOf = (value, separator) => (value ? String(value).split(separator) : []);

/** The shared filter list (optional sort row + All + options) — used by header menus and filter pills. */
export function FilterMenuBody({ options, value, onChange, onSort, testid, multi, separator = ",", close, searchable = false }) {
  const [q, setQ] = useState("");
  const picks = picksOf(value, separator);
  const shown = q.trim() ? options.filter((o) => String(o.label).toLowerCase().includes(q.trim().toLowerCase())) : options;
  const on = (v) => picks.includes(v);
  const pick = (v) => {
    if (v === "") { onChange(""); close(); return; }
    if (!multi) { onChange(v === value ? "" : v); close(); return; }
    onChange((on(v) ? picks.filter((p) => p !== v) : [...picks, v]).join(separator));
  };
  return (
    <>
      {onSort && <div className="flex border-b border-slate-100 text-[11px]">
        <button onClick={() => { onSort("asc"); close(); }}
                className="flex-1 px-2 py-1.5 text-slate-500 hover:bg-slate-50"
                data-testid={`col-sort-asc-${testid}`}>Sort A → Z</button>
        <button onClick={() => { onSort("desc"); close(); }}
                className="flex-1 border-l border-slate-100 px-2 py-1.5 text-slate-500 hover:bg-slate-50"
                data-testid={`col-sort-desc-${testid}`}>Z → A</button>
      </div>}
      {searchable && (
        <div className="border-b border-slate-100 p-1.5">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." data-testid={`col-search-${testid}`}
                 className="h-7 w-full rounded border border-slate-200 bg-transparent px-2 text-xs outline-none focus:border-[#007BFF]" />
        </div>)}
      <div className="max-h-64 overflow-auto py-1">
        {!q.trim() && <button onClick={() => pick("")}
                className="flex w-full items-center justify-between px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
                data-testid={`col-option-all-${testid}`}>
          All
          {!value && <Check className="h-3 w-3 text-[#007BFF]" />}
        </button>}
        {shown.map((o) => (
          <button key={o.value} onClick={() => pick(o.value)}
                  className={`flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-xs hover:bg-slate-50 ${
                    on(o.value) ? "text-[#007BFF]" : "text-slate-600"}`}
                  data-testid={`col-option-${testid}-${o.value}`}>
            <span className="truncate">{o.label}</span>
            <span className="flex items-center gap-1.5 shrink-0">
              {o.count !== undefined && <span className="tabular-nums text-slate-400">{o.count}</span>}
              {on(o.value) && <Check className="h-3 w-3" />}
            </span>
          </button>
        ))}
        {!shown.length && <div className="px-2.5 py-2 text-xs text-slate-400">No matches</div>}
      </div>
    </>
  );
}

const SortArrow = ({ sortActive, direction }) => (sortActive
  ? (direction === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
  : <ArrowDown className="h-3 w-3 opacity-0 transition-opacity group-hover/th:opacity-40" />);

/** A grid column header that filters and sorts from its own menu, so the toolbar stays empty.
 *  `sortOnLabel`: the label click toggles the sort; a small filter icon opens the menu. */
export default function HeaderFilterMenu({
  label, options, value, onChange, onSort, sortActive, direction, testid, multi = false, separator = ",",
  open: openProp, onOpenChange, sortOnLabel = false, searchable = false, hideCount = false,
}) {
  const [openState, setOpenState] = useState(false);
  // Optional controlled open — lets a parent keep the menu open across header re-mounts.
  const open = openProp ?? openState;
  const setOpen = onOpenChange || setOpenState;
  const count = !hideCount && multi && picksOf(value, separator).length > 1 ? ` (${picksOf(value, separator).length})` : "";
  const body = (
    <PopoverContent align="start" className="w-56 p-0" data-testid={`col-menu-${testid}`}>
      <FilterMenuBody options={options} value={value} onChange={onChange} onSort={sortOnLabel ? null : onSort}
                      testid={testid} multi={multi} separator={separator} close={() => setOpen(false)} searchable={searchable} />
    </PopoverContent>
  );

  if (sortOnLabel) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <span className="group/th inline-flex items-center gap-1">
          <button type="button" onClick={() => onSort(sortActive && direction === "asc" ? "desc" : "asc")}
                  className={`inline-flex items-center gap-1 font-medium ${value ? "text-[#007BFF]" : "text-slate-500 hover:text-slate-800"}`}
                  data-testid={`col-sort-${testid}`}>
            {label}{count}<SortArrow sortActive={sortActive} direction={direction} />
          </button>
          <PopoverTrigger asChild>
            <button type="button" title={`Filter ${label}`} data-testid={`col-filter-${testid}`}
                    className={`rounded p-0.5 transition-colors hover:bg-slate-100 ${value ? "text-[#007BFF]" : "text-slate-400 hover:text-slate-700"}`}>
              <ListFilter className="h-3 w-3" />
            </button>
          </PopoverTrigger>
        </span>
        {body}
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`group/th inline-flex items-center gap-1 font-medium ${
          value ? "text-[#007BFF]" : "text-slate-500 hover:text-slate-800"}`}
                data-testid={`col-filter-${testid}`}>
          {label}{count}
          {value ? <Filter className="h-3 w-3" /> : <SortArrow sortActive={sortActive} direction={direction} />}
        </button>
      </PopoverTrigger>
      {body}
    </Popover>
  );
}
